import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { OverFullWindow, SidePanel } from 'components/UI';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import ClientModel from '@spike/client-model';
import { ClientsStatus } from '@spike/clients-action';
import useNonInitialEffect from '@versiondos/hooks';
import { PetsStatus } from '@spike/pets-action';
import { useApiClientWrapper, useAuth, useMarketplace } from 'hooks';
import Pet from '@spike/pet-model';
import MainStep from './MainStep';
import EditStep from './EditStep';
import { cloneDeep } from 'lodash';
import { deleteEnrollmentThunk, fetchMembershipsThunk } from '@spike/memberships-action';
import { ConsumedPreset, Membership } from '@spike/membership-model';
import Client from 'pages/Client';
import Checkout from 'components/Checkout2';
import { PaymentMethodIds } from '@spike/payments-model';
import { Moment } from 'moment-timezone';

export interface SellMembershipProps {
    onClose: () => void;
    className?: string;
    onPaid?: () => void;
    clientId?: number;
    membershipId?: number;
    fromClientMemberships?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        clientOverWindow: {
            zIndex: 1001,
            [theme.breakpoints.down('sm')]: {
                left: 0
            },
            [theme.breakpoints.only('md')]: {
                left: '65px'
            },
            [theme.breakpoints.only('lg')]: {
                left: '80px'
            },
            [theme.breakpoints.only('xl')]: {
                left: '80px'
            }
        }
    })
);

enum Steps {
    MAIN = 'main',
    EDIT = 'edit'
}

export interface SellInfo {
    clientId?: number;
    petIds: Array<number>;
    membershipId?: number;
    startAt?: Moment | null;
    endAt?: Moment | null;
    consumedPreset?: ConsumedPreset;
    priceOverride?: number;
}

export const SellMembership: FunctionComponent<SellMembershipProps> = props => {
    const classes = useStyles(props);
    const marketplace = useMarketplace();
    const marketplaceId = marketplace.id;

    const auth = useAuth();
    const accessToken = auth.token;

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);

    const memberships = useSelector<RootState, Array<Membership>>(state => state.memberships.list);

    const pets = useSelector<RootState, Array<Pet>>(state => state.pets.list);
    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);

    const activeMemberships = useMemo(
        () =>
            memberships
                .filter(membership => membership.active && !membership.deleted)
                .sort((membership, otherMembership) => membership.name.localeCompare(otherMembership.name)),
        [memberships]
    );
    const activePets = useMemo(() => pets.filter(pet => pet.active && !pet.deleted && !pet.deceased), [pets]).sort(
        (pet, otherPet) => pet.name.localeCompare(otherPet.name)
    );

    const [loading, setLoading] = useState({ client: false, pets: false });
    const [sellInfo, setSellInfo] = useState<SellInfo>({
        petIds: [],
        clientId: props.clientId,
        membershipId: props.membershipId
    });

    const [step, setStep] = useState<Steps>(Steps.MAIN);

    const [show, setShow] = useState<'sell' | 'newClient' | 'newPet' | 'checkout'>('sell');

    const [checkoutInfo, setCheckoutInfo] = useState<{
        orderId: number | null;
        enrollmentId: number | null;
        paymentMethods: Array<PaymentMethodIds> | undefined;
        hide: boolean;
    }>({ orderId: null, enrollmentId: null, paymentMethods: undefined, hide: false });

    const [editClientId, setEditClientId] = useState<number | null>(null);

    useEffect(() => {
        const handleUnload = () => {
            const blob = new Blob([''], {});
            const url = `${process.env.REACT_APP_HOST_URL}${process.env.REACT_APP_API_BASE_URL}enrollments/${checkoutInfo.enrollmentId}/delete?marketplace_id=${marketplaceId}&access_token=${accessToken}`;
            checkoutInfo.enrollmentId && navigator.sendBeacon(url, blob);
        };

        window.addEventListener('unload', handleUnload);

        return () => {
            window.removeEventListener('unload', handleUnload);
        };
    }, [checkoutInfo.enrollmentId]);

    useEffect(() => {
        dispatch(fetchMembershipsThunk(apiClientWrapper, 1, 100, 'true', ''));
    }, []);

    useNonInitialEffect(() => {
        switch (clientsStatus) {
            case ClientsStatus.GetSuccess:
            case ClientsStatus.Error:
                setLoading(prev => ({ ...prev, client: false }));
                break;
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        switch (petsStatus) {
            case PetsStatus.FetchSuccess:
            case PetsStatus.Error:
                setLoading(prev => ({ ...prev, pets: false }));
                break;
        }
    }, [petsStatus]);

    useEffect(() => {
        if (props.membershipId === undefined) {
            return;
        }

        setSellInfo(prev => (prev.membershipId === undefined ? { ...prev, membershipId: props.membershipId } : prev));
    }, [props.membershipId]);

    const selectedPets = useMemo(
        () => pets.filter(pet => sellInfo.petIds.includes(pet.id ?? 0)),
        [pets, sellInfo.petIds]
    );

    const save = (sellInfo: SellInfo) => {
        setSellInfo(cloneDeep(sellInfo));
        setStep(Steps.MAIN);
    };

    const setNewClientId = (clientId?: number) =>
        setSellInfo(prev => ({ ...prev, clientId, petIds: [], membershipId: prev.membershipId ?? props.membershipId }));

    const setNewPetId = (clientId: number, petId?: number) =>
        setSellInfo(prev => ({
            ...prev,
            clientId,
            petIds: petId ? [petId] : [],
            membershipId: prev.membershipId ?? props.membershipId
        }));

    const savedClientHandler = (client: ClientModel) => {
        setNewClientId(client.id);
        setShow('sell');
    };

    const savedPetHandler = (pet: Pet) => {
        setNewPetId(pet.clientId, pet.id);
        setShow('sell');
    };

    const checkoutHandler = (orderId: number, enrollmentId: number, enrollmentType: string) => {
        const paymentMethods =
            enrollmentType === 'package'
                ? [
                      PaymentMethodIds.Cash,
                      PaymentMethodIds.CreditCard,
                      PaymentMethodIds.POS,
                      PaymentMethodIds.PayByLink,
                      PaymentMethodIds.Other
                  ]
                : enrollmentType === 'subscription'
                ? [PaymentMethodIds.CreditCard, PaymentMethodIds.POS, PaymentMethodIds.PayByLink]
                : undefined;

        setCheckoutInfo(prev => ({ ...prev, orderId, enrollmentId, paymentMethods, hide: false }));
        setShow('checkout');
    };

    const deleteEnrollment = () => {
        if (checkoutInfo.enrollmentId) {
            dispatch(deleteEnrollmentThunk(apiClientWrapper, checkoutInfo.enrollmentId));
        }
    };

    const startPaymentCheckoutHandler = () => {
        setCheckoutInfo(prev => ({ ...prev, enrollmentId: null }));
    };

    const closeCheckoutHandler = () => {
        deleteEnrollment();
        props.onClose();
    };

    const backCheckoutHandler = () => {
        deleteEnrollment();
        setCheckoutInfo(prev => ({
            ...prev,
            orderId: null,
            enrollmentId: null,
            paymentMethods: undefined,
            hide: false
        }));
        setShow('sell');
    };

    const editClientHandler = (clientId: number) => {
        setEditClientId(clientId);
        setCheckoutInfo(prev => ({ ...prev, hide: true }));
    };

    const closeClientHandler = () => {
        setEditClientId(null);
        setCheckoutInfo(prev => ({ ...prev, hide: false }));
    };
    return (
        <>
            {show === 'sell' && (
                <SidePanel
                    side="right"
                    showCloseButton={true}
                    onClose={props.onClose}
                    disableEnforceFocus
                >
                    {step === Steps.MAIN && (
                        <MainStep
                            sellInfo={sellInfo}
                            fromClientMemberships={props.fromClientMemberships}
                            onChange={setSellInfo}
                            onClose={props.onClose}
                            onEdit={() => setStep(Steps.EDIT)}
                            onAddClient={() => setShow('newClient')}
                            onCheckout={checkoutHandler}
                        />
                    )}
                    {step === Steps.EDIT && (
                        <EditStep
                            pets={activePets}
                            sellInfo={sellInfo}
                            memberships={activeMemberships}
                            onSave={save}
                            onClose={props.onClose}
                            onBack={() => setStep(Steps.MAIN)}
                            onAddPets={() => setShow('newPet')}
                        />
                    )}
                </SidePanel>
            )}
            {show === 'checkout' && checkoutInfo.orderId && (
                <Checkout
                    orderId={checkoutInfo.orderId}
                    paymentMethods={checkoutInfo.paymentMethods}
                    hideTips={true}
                    hide={checkoutInfo.hide}
                    onClose={closeCheckoutHandler}
                    onBack={backCheckoutHandler}
                    onStartPayment={startPaymentCheckoutHandler}
                    onPaid={() => {
                        props.onClose();
                        props.onPaid?.();
                    }}
                    onPayByLinkSent={closeCheckoutHandler}
                    onPayByLinkCreated={props.onPaid}
                    onEditClient={editClientHandler}
                />
            )}
            {['newClient', 'newPet'].includes(show) && (
                <OverFullWindow
                    className={classes.clientOverWindow}
                    onClose={() => setShow('sell')}
                >
                    <Client
                        hideHeader={true}
                        clientId={sellInfo.clientId}
                        isNewBooking={true}
                        onClose={() => setShow('sell')}
                        onClientSaved={savedClientHandler}
                        onPetSaved={savedPetHandler}
                        onDelete={() => {}}
                    />
                </OverFullWindow>
            )}
            {editClientId !== null && (
                <OverFullWindow onClose={closeClientHandler}>
                    <Client
                        clientId={editClientId}
                        hideHeader={true}
                        onCloseModal={closeClientHandler}
                        onClientSaved={closeClientHandler}
                    />
                </OverFullWindow>
            )}
        </>
    );
};

export default SellMembership;
